import React from 'react';
import { ArrowUpRight, Award, Newspaper, Radio, Instagram, Linkedin, Sparkles, TrendingUp, Trophy, Medal, Star, Crown } from 'lucide-react';

/**
 * Curated press & media coverage of EvolveBPM.
 * Editorial "In the news" style — publication wordmark, headline, hover-lift card.
 */

const MEDIA = [
  {
    outlet: 'Forbes India',
    kind: 'Feature',
    headline: 'EvolveBPM wins Best Emerging Company Award from Business Mint.',
    url: 'https://www.forbesindia.com/article/brand-connect/evolvebpm-won-the-best-emerging-company-award-from-business-mint/60905/1',
    icon: Award,
    accent: '#FFB020',
    featured: true,
  },
  {
    outlet: 'The Economic Times',
    kind: 'Video · Feature',
    headline: "India\u2019s BPM industry is no longer just outsourcing \u2014 it\u2019s leading AI-driven transformation.",
    url: 'https://economictimes.indiatimes.com/industry/services/consultancy-/-audit/indias-bpm-industry-is-no-longer-just-outsourcing-its-leading-ai-driven-transformation/videoshow/132112958.cms?from=mdr',
    icon: Newspaper,
    accent: '#4ECDC4',
    featured: true,
  },
  {
    outlet: 'Financial Express',
    kind: 'Founder profile',
    headline: 'Meet Satish K Sadasivan \u2014 the man behind Evolve Global Corp.',
    url: 'https://www.financialexpress.com/life/lifestyle-meet-satish-k-sadasivan-the-man-behind-evolve-global-corp-know-about-his-journey-education-and-more-3162247/',
    icon: Newspaper,
    accent: '#4ECDC4',
  },
  {
    outlet: 'Business Standard',
    kind: 'Recognition',
    headline: 'Satish Sadasivan honoured with the \u201cMost Admired Global Indians 2020\u201d title.',
    url: 'https://www.business-standard.com/content/press-releases-ani/satish-sadasivan-was-glorified-with-the-title-the-most-admired-global-indians-2020-121012701089_1.html',
    icon: Award,
    accent: '#FFB020',
  },
  {
    outlet: 'PR Newswire',
    kind: 'Press release',
    headline: 'EvolveBPM LLC announces new corporate structure.',
    url: 'https://www.prnewswire.com/in/news-releases/evolvebpm-llc-announces-new-corporate-structure-301665919.html',
    icon: Radio,
    accent: '#4ECDC4',
  },
  {
    outlet: 'ANI News',
    kind: 'Launch coverage',
    headline: 'Revenue growth strategy and solutions at EvolveBPM launched by Silicon Valley veterans.',
    url: 'https://www.aninews.in/news/business/revenue-growth-strategy-and-solutions-at-evolvebpm-launched-by-silicon-valley-veterans20200617162412/',
    icon: Radio,
    accent: '#4ECDC4',
  },
  {
    outlet: 'Nationwide Awards',
    kind: 'Award · 2020',
    headline: 'Best Emerging Company \u2014 Revenue Growth Solutions Category.',
    url: 'https://nationwideawards.org/winners_list_view/EVOLVE-BPM-opc-PRIVATE-LIMITED-Best-Emerging-Company-2020-Revenue-Growth-Solutions-Category',
    icon: Award,
    accent: '#FFB020',
  },
  {
    outlet: 'SalesTechStar',
    kind: 'Industry press',
    headline: 'EvolveBPM won the Best Emerging Company Award from Business Mint.',
    url: 'https://salestechstar.com/price-optimization-revenue-management/evolvebpm-won-the-best-emerging-company-award-from-business-mint/',
    icon: TrendingUp,
    accent: '#4ECDC4',
  },
  {
    outlet: 'LinkedIn',
    kind: 'Rankings',
    headline: 'EvolveBPM soars in Global B2B Marketing rankings.',
    url: 'https://www.linkedin.com/posts/evolve-bpm_evolvebpm-soars-in-global-b2b-marketing-rankings-activity-7212101915143200772-KyM1/',
    icon: Linkedin,
    accent: '#4ECDC4',
  },
  {
    outlet: 'B2B Marketing',
    kind: 'Industry',
    headline: 'B2B agency growth \u2014 EvolveBPM featured.',
    url: 'https://www.b2bmarketing.net/b2b-agency-growth/',
    icon: TrendingUp,
    accent: '#4ECDC4',
  },
  {
    outlet: 'BRANDZ Magazine',
    kind: 'Leader profile',
    headline: 'The Innovative Visionary and a Celebrated International Leader.',
    url: 'https://brandzmagazine.com/the-innovative-visionary-and-a-celebrated-international-leader/',
    icon: Sparkles,
    accent: '#FFB020',
  },
  {
    outlet: 'Instagram',
    kind: 'Social',
    headline: 'EvolveBPM on Instagram \u2014 latest post.',
    url: 'https://www.instagram.com/p/C87EnKfyCAn/',
    icon: Instagram,
    accent: '#FFB020',
  },
];

export default function Media() {
  const featured = MEDIA.filter((m) => m.featured);
  const rest = MEDIA.filter((m) => !m.featured);

  return (
    <section id="media" className="py-20 lg:py-32 bg-[#050A1F] text-[#F1EDE4] border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-[#FFB020] mb-4">
              <span className="w-6 h-px bg-[#FFB020]" />
              <span>Media &amp; Press</span>
            </div>
            <h2 className="text-4xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.02] font-medium">
              As seen &amp; recognised <span className="font-serif italic font-normal text-[#4ECDC4]">across the industry.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-white/60 text-[15px] leading-relaxed">
              A decade of B2B demand-gen leadership — covered by the outlets that matter and recognised with awards from Forbes India, Business Mint, and Nationwide Awards.
            </p>
          </div>
        </div>

        {/* Featured — 2 large tiles */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {featured.map((m) => {
            const Icon = m.icon;
            return (
              <a
                key={m.url}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`media-featured-${m.outlet.replace(/\s+/g, '-').toLowerCase()}`}
                className="group relative overflow-hidden bg-gradient-to-br from-[#0F1C40] to-[#0A1024] border border-white/10 hover:border-white/25 rounded-2xl p-8 lg:p-10 transition-colors"
              >
                <div
                  className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none opacity-40 group-hover:opacity-60 transition-opacity"
                  style={{ background: `radial-gradient(circle, ${m.accent}55, transparent 70%)` }}
                />
                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: `${m.accent}22`, border: `1px solid ${m.accent}55`, color: m.accent }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <ArrowUpRight
                      className="w-5 h-5 opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                      style={{ color: m.accent }}
                    />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: m.accent }}>
                    {m.outlet} · {m.kind}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-serif italic leading-tight text-[#F1EDE4]">
                    {m.headline}
                  </h3>
                </div>
              </a>
            );
          })}
        </div>

        {/* Awards & Recognition strip */}
        <div className="mb-6" data-testid="media-awards-strip">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFB020]">
              Awards &amp; Recognition
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-[#FFB020]/40 to-transparent" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                icon: Trophy,
                title: 'Best Emerging Company',
                year: '2020',
                body: 'Nationwide Awards',
                accent: '#FFB020',
              },
              {
                icon: Crown,
                title: 'Most Admired Global Indians',
                year: '2020',
                body: 'Business Standard',
                accent: '#4ECDC4',
              },
              {
                icon: Star,
                title: 'Global B2B Marketing Top-Ranked',
                year: '2024',
                body: 'LinkedIn',
                accent: '#FFB020',
              },
              {
                icon: Medal,
                title: 'Brand Connect Feature',
                year: '2024',
                body: 'Forbes India',
                accent: '#4ECDC4',
              },
            ].map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  className="relative bg-gradient-to-br from-[#0F1C40] via-[#0A1024] to-[#050A1F] border border-white/10 hover:border-white/20 rounded-xl p-4 lg:p-5 transition-colors overflow-hidden group"
                >
                  <div
                    className="absolute -top-8 -right-8 w-24 h-24 rounded-full pointer-events-none opacity-30 group-hover:opacity-60 transition-opacity"
                    style={{ background: `radial-gradient(circle, ${a.accent}66, transparent 70%)` }}
                  />
                  <div className="relative flex items-center gap-3">
                    <div
                      className="relative w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                      style={{
                        background: `linear-gradient(135deg, ${a.accent}33, ${a.accent}11)`,
                        border: `1.5px solid ${a.accent}66`,
                        color: a.accent,
                        boxShadow: `0 0 20px ${a.accent}33 inset`,
                      }}
                    >
                      <Icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] lg:text-[12px] text-[#F1EDE4] font-semibold leading-tight">
                        {a.title}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span
                          className="text-[9px] font-mono uppercase tracking-widest"
                          style={{ color: a.accent }}
                        >
                          {a.year}
                        </span>
                        <span className="text-white/25 text-[9px]">·</span>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-white/45">
                          {a.body}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rest — compact list rows */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {rest.map((m, i) => {
            const Icon = m.icon;
            return (
              <a
                key={m.url}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`media-item-${m.outlet.replace(/\s+/g, '-').toLowerCase()}`}
                className={`group flex items-center gap-4 lg:gap-6 px-5 lg:px-8 py-5 hover:bg-white/[0.03] transition-colors ${
                  i !== rest.length - 1 ? 'border-b border-white/8' : ''
                }`}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${m.accent}18`, border: `1px solid ${m.accent}40`, color: m.accent }}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: m.accent }}>
                      {m.outlet}
                    </span>
                    <span className="text-[9px] font-mono uppercase tracking-widest text-white/30">
                      {m.kind}
                    </span>
                  </div>
                  <div className="text-[14px] lg:text-[15px] text-[#F1EDE4]/85 group-hover:text-[#F1EDE4] leading-snug mt-0.5">
                    {m.headline}
                  </div>
                </div>
                <ArrowUpRight
                  className="w-4 h-4 text-white/30 group-hover:text-[#4ECDC4] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0"
                />
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
